"use client";

import { m as motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { defaultViewport, fastTransition } from "@/lib/motion";

const sessions = [
  {
    number: 1,
    title: "Understanding Your AI Readiness",
    description: "The six forces that shape whether AI deployments stick — or stall.",
  },
  {
    number: 2,
    title: "Selecting a Use Case with Task-Tech Fit",
    description: "Five practical filters for picking work where AI earns trust and ROI.",
  },
  {
    number: 3,
    title: "Creating an AI Assistant Proof-of-Concept",
    description: "Define the job, constrain the behavior, test with real scenarios.",
  },
  {
    number: 4,
    title: "Preparing Knowledge for AI",
    description: "Extract, clean, and structure what the model can use — so answers are useful.",
  },
  {
    number: 5,
    title: "Preparing to Scale",
    description: "Turn a working demo into adoption with a concrete rollout plan.",
  },
];

export const WhatYouLearnSection = () => {
  return (
    <div className="section-inner">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl lg:text-5xl">
          <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
            5 Sessions.
          </span>{" "}
          One Clear Path.
        </h2>
        <p className="text-muted-foreground text-lg mt-3 max-w-2xl mx-auto">
          From readiness assessment to a scaled action plan — here's what each session covers.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
        {sessions.map((s, i) => (
          <motion.div
            key={s.number}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ ...fastTransition, delay: i * 0.06 }}
            className="relative border-2 border-border/60 rounded-xl p-5 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-[border-color,box-shadow] duration-300 group"
          >
            {/* Gradient top accent */}
            <div className="absolute top-0 left-4 right-4 h-0.5 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary text-sm font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                {s.number}
              </span>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Session
              </span>
            </div>
            <h3 className="text-base font-bold leading-snug mb-2 group-hover:text-primary transition-colors">{s.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {s.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Full Curriculum CTA */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={defaultViewport}
        transition={{ ...fastTransition, delay: 0.35 }}
        className="text-center"
      >
        <Link
          to="/curriculum"
          className="group inline-flex items-center gap-2.5 px-6 py-3 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-primary/10"
        >
          <BookOpen className="w-4.5 h-4.5" />
          Explore the Full Curriculum
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </motion.div>
    </div>
  );
};
